import { createStateHook, type HookStateFunction } from 'react-sweet-state';

import type { ConversationAction } from '@atlassian/rovo-conversation-actions/types';

import {
	type AISmartButtonState,
	AISmartButtonStore,
	type DailyBriefUnread,
} from '../../ai-smart-button-store';

/**
 * Hook to access nudges from the AISmartButton state.
 */
export const useAINudges: HookStateFunction<ConversationAction[], void> = createStateHook(
	AISmartButtonStore,
	{
		selector: (state: AISmartButtonState) => state.nudges,
	},
);

/**
 * Hook to access button clicked timestamp from the AISmartButton state.
 */
export const useAIButtonClickedTimestamp: HookStateFunction<number | undefined, void> =
	createStateHook(AISmartButtonStore, {
		selector: (state: AISmartButtonState) => state.buttonClickedTimestamp,
	});

/**
 * Hook to access the daily brief unread status from the AISmartButton state.
 */
export const useDailyBriefUnread: HookStateFunction<DailyBriefUnread, void> = createStateHook(
	AISmartButtonStore,
	{
		selector: (state: AISmartButtonState) => state.dailyBriefUnread,
	},
);

/**
 * Hook to access whether a nudge bubble is currently visible on screen
 * (TREX-1601). True when `AIProactiveNudge` (or any of its variants such
 * as the FOMO nudge) is rendered and shown. Consumed by `AISmartButton`
 * to suppress the mouse-proximity color-cycle animation while a nudge
 * is on screen, so the two visual effects don't compete for the same
 * button surface.
 */
export const useIsNudgeVisible: HookStateFunction<boolean, void> = createStateHook(
	AISmartButtonStore,
	{
		selector: (state: AISmartButtonState) => state.isNudgeVisible,
	},
);
